import React from "react"
import PropTypes from "prop-types"
import { AutoComplete, Flex, Form } from "antd"
import { Icon } from "ui/Icon"
import { Text } from "ui/Typography"
import { CustomForm } from "ui/Form"
import { CustomInput } from "ui/Input"
import { useTranslation } from "react-i18next"
import styles from "./Search.module.css"
import { colors } from "themes/variables"
import { useDispatch, useSelector } from "react-redux"
import { errorFieldsSelector } from "modules/errors/selectors"
import { CustomInputError } from "ui/CustomInputError"
import { receiveErrors } from "modules/errors/reducer"
import { getName } from "country-list"

export const AddressesSearch = ({ setManualAddressEnabled, form, initialValues, externalAddress, onCoordsChange, onFinish }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [options, setOptions] = React.useState([])
  const [autocompleteService, setAutocompleteService] = React.useState(null)
  const [formattedAddress, setFormattedAddress] = React.useState()
  const errors = useSelector(errorFieldsSelector())

  const onSelect = (value) => {
    if (value === "enter-manually") {
      setManualAddressEnabled()
      return
    }

    const selectedPlace = options.find((option) => option.value === value)

    if (selectedPlace && selectedPlace.id) {
      fetchPlaceDetails(selectedPlace.id)
      setFormattedAddress(selectedPlace.value)

      dispatch(
        receiveErrors({
          data: {
            message: {
              addresses: [],
              street_address: [],
              city: [],
              state: [],
              zip_code: [],
              country: []
            }
          },
          showToast: false
        })
      )
    }
  }

  const fetchPlaceDetails = (placeId) => {
    if (!placeId) return

    const service = new window.google.maps.places.PlacesService(document.createElement("div"))
    service.getDetails(
      {
        placeId: placeId
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          let lat = null
          let lng = null
          if (place.geometry && place.geometry.location) {
            lat = place.geometry.location.lat()
            lng = place.geometry.location.lng()
            if (onCoordsChange) {
              onCoordsChange({ lat, lng })
            }
          }

          if (place.address_components) {
            const streetNumber = place.address_components.find((component) => component.types.includes("street_number"))?.long_name || ""
            const streetAddress = place.address_components.find((component) => component.types.includes("route"))?.long_name || ""
            const city = place.address_components.find((component) => component.types.includes("locality"))?.long_name || ""
            const state =
              place.address_components.find((component) => component.types.includes("administrative_area_level_1"))?.long_name || ""
            const postalCode = place.address_components.find((component) => component.types.includes("postal_code"))?.long_name || ""
            const country = place.address_components.find((component) => component.types.includes("country"))?.short_name || ""

            const fieldsToSet = [
              { name: "street_address", value: `${streetNumber} ${streetAddress}`.trim(), touched: true, errors: [] },
              { name: "city", value: city, touched: true, errors: [] },
              { name: "state", value: state, touched: true, errors: [] },
              { name: "zip_code", value: postalCode, touched: true, errors: [] },
              { name: "country", value: country, touched: true, errors: [] }
            ]

            if (lat !== null && lng !== null) {
              fieldsToSet.push(
                { name: "latitude", value: lat, touched: true, errors: [] },
                { name: "longitude", value: lng, touched: true, errors: [] }
              )
            }

            form.setFields(fieldsToSet)

            const fieldsToValidate = country === "BS"
              ? ["country"]
              : ["street_address", "city", "state", "zip_code", "country"]
            form.validateFields(fieldsToValidate).catch(() => {})
          }

          if (place.formatted_address) {
            setFormattedAddress(place.formatted_address)
          }
        }
      }
    )
  }

  React.useEffect(() => {
    const initAutocompleteService = () => {
      if (window?.google?.maps?.places) {
        const service = new window.google.maps.places.AutocompleteService()
        setAutocompleteService(service)
      } else {
        setTimeout(initAutocompleteService, 100)
      }
    }

    initAutocompleteService()
  }, [])

  const { street_address, city, state, zip_code, country } = initialValues

  React.useEffect(() => {
    let cancelled = false

    if (externalAddress) {
      setFormattedAddress(externalAddress)
      return () => { cancelled = true }
    }

    const countryName = country ? getName(country) : ""
    const address = [street_address, city, state, zip_code, countryName].reduce((accumulator, currentValue) => {
      if (currentValue) {
        if (accumulator.length > 0) {
          accumulator += ", "
        }
        accumulator += currentValue
      }
      return accumulator
    }, "")

    if (address) {
      setFormattedAddress(address)

      if (window?.google?.maps && onCoordsChange) {
        const geocoder = new window.google.maps.Geocoder()
        geocoder.geocode({ address }, (results, status) => {
          if (cancelled) return
          if (form.getFieldValue("latitude")) return
          if (status === "OK" && results[0]) {
            const location = results[0].geometry.location
            onCoordsChange({
              lat: location.lat(),
              lng: location.lng()
            })
          }
        })
      }
    }

    return () => { cancelled = true }
  }, [street_address, city, state, zip_code, country, externalAddress, form, onCoordsChange])

  const clearAddressFields = () => {
    setFormattedAddress(undefined)
    if (onCoordsChange) {
      onCoordsChange(null)
    }
    form.setFields([
      { name: "street_address", value: "", touched: true },
      { name: "city", value: "", touched: true },
      { name: "state", value: "", touched: true },
      { name: "zip_code", value: "", touched: true },
      { name: "country", value: "", touched: true }
    ])
    if (onFinish) {
      onFinish(form.getFieldsValue(), () => {})
    }
  }

  const handleSearch = (value) => {
    if (!value) {
      setOptions([])
      return
    }

    if (!autocompleteService) {
      return
    }

    autocompleteService.getPlacePredictions(
      {
        input: value
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const formattedOptions = predictions.map((prediction) => {
            const highlightedDescription = prediction.description.replace(
              new RegExp(`(${value})`, "gi"),
              (match) => `<span class="fw-500 pre-wrap">${match}</span>`
            )

            return {
              value: prediction.description,
              label: (
                <Flex>
                  <Icon type="feather" name="MapPin" width={18} height={18} color={colors.textSecondary} className="mt-xxs" />
                  <div className={`${styles.addressInput} ml-xs`}>
                    <div className="flex-wrap" dangerouslySetInnerHTML={{ __html: highlightedDescription }} />
                  </div>
                </Flex>
              ),
              id: prediction.place_id
            }
          })

          formattedOptions.push({
            value: "enter-manually",
            label: (
              <div className="align-center">
                <Text className="ml-xs" variant="link">
                  {t("pro.addresses.enter_manually")}
                </Text>
              </div>
            ),
            id: "enter-manually"
          })

          setOptions(formattedOptions)
        } else {
          setOptions([])
        }
      }
    )
  }

  return (
    <>
      <div className={styles.searchInput}>
        <Icon className={styles.pinIcon} type="feather" name="MapPin" width={18} height={18} />
        <AutoComplete
          placeholder="Type your address here"
          options={options}
          allowClear={{ clearIcon: <Icon type="feather" name="XCircle" width={18} height={18} /> }}
          onSelect={onSelect}
          onSearch={handleSearch}
          onChange={(val) => {
            setFormattedAddress(val)
            if (!val) {
              clearAddressFields()
            }
          }}
          onClear={clearAddressFields}
          value={formattedAddress}
        />
      </div>
      <CustomForm form={form} name="profile-information" layout="vertical" initialValues={initialValues} style={{ display: "none" }}>
        <Form.Item label={t("pro.addresses.street_address")} name="street_address">
          <CustomInput />
        </Form.Item>

        <Form.Item label={t("pro.addresses.country")} name="country" required={true}>
          <CustomInput />
        </Form.Item>

        <Form.Item label={t("pro.addresses.state")} name="state" required={true}>
          <CustomInput />
        </Form.Item>

        <Form.Item label={t("pro.addresses.city")} name="city" required={true}>
          <CustomInput />
        </Form.Item>
        <Form.Item label={t("pro.addresses.zip")} name="zip_code">
          <CustomInput />
        </Form.Item>
        <Form.Item name="latitude" hidden>
          <CustomInput />
        </Form.Item>
        <Form.Item name="longitude" hidden>
          <CustomInput />
        </Form.Item>
      </CustomForm>
      {errors.addresses && <CustomInputError errorKey={errors.addresses[0]} />}
    </>
  )
}

AddressesSearch.propTypes = {
  setManualAddressEnabled: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  initialValues: PropTypes.shape({
    street_address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip_code: PropTypes.string,
    country: PropTypes.string
  }).isRequired,
  externalAddress: PropTypes.string,
  onCoordsChange: PropTypes.func,
  onFinish: PropTypes.func
}
